/* TEST 2026-09-04: desktop PDP gallery behaviors (works with the desktop
   rules in commune-pdp-media-bg.css). Three jobs:
   1. Floating down-chevron on the thumbnail rail — visible only while more
      thumbnails sit below the rail's fold, hides at the end of the list.
      Native scrolling on the rail keeps working.
   2. --pdp-chrome-visible on the media column — the on-screen height of the
      in-flow top chrome (announcement bar + header), so the CSS can size the
      media frame to end exactly at the fold at any scroll position.
   3. Slide changes on the main PDP gallery jump instantly and fade in,
      instead of the theme's animated horizontal scroll.
   4. The lightbox (zoom-dialog) opens and closes with a plain fade instead
      of the theme's view-transition morph, and thumbnail clicks inside it
      jump instantly with a fade instead of smooth-scrolling. Layout rules
      (one contained image per viewport) live in commune-pdp-media-bg.css.
   To revert: delete this file + its line in snippets/scripts.liquid. */

const RAIL_SELECTOR =
  'main[data-template^="product"] media-gallery slideshow-controls[thumbnails][pagination-position="left"]';

const CHEVRON_SVG = `
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M2.5 5l4.5 4.5L11.5 5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`;

function setupRail(rail) {
  if (rail.querySelector('.commune-thumb-chevron')) return;

  const container = rail.querySelector('.slideshow-controls__thumbnails-container');
  const list = rail.querySelector('.slideshow-controls__thumbnails');
  if (!container || !list) return;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'commune-thumb-chevron is-hidden';
  button.setAttribute('aria-label', 'Scroll to more thumbnails');
  button.innerHTML = CHEVRON_SVG;
  rail.appendChild(button);

  const update = () => {
    const overflow = container.scrollHeight - container.clientHeight;
    const atEnd = container.scrollTop >= overflow - 4;
    button.classList.toggle('is-hidden', overflow <= 4 || atEnd);
  };

  button.addEventListener('click', () => {
    const thumb = list.querySelector('.slideshow-control');
    const gap = parseFloat(getComputedStyle(list).rowGap) || 0;
    const step = thumb ? (thumb.offsetHeight + gap) * 2 : container.clientHeight / 2;
    container.scrollBy({ top: step, behavior: 'smooth' });
  });

  container.addEventListener('scroll', update, { passive: true });
  const observer = new ResizeObserver(update);
  observer.observe(container);
  observer.observe(list); // thumbnail heights settle as images load
  update();
}

/* Tag each slide with its photo's aspect ratio — the CSS crop cap needs it
   and the theme doesn't put it on the slide. Ratio is constant across
   srcset variants, so tagging once per slide is enough. Also classifies
   each photo as canvas-style or full-bleed (see detectBleed). */
function tagSlideRatios() {
  const slides = [
    ...document.querySelectorAll(
      'main[data-template^="product"] media-gallery.media-gallery--carousel slideshow-slide.product-media-container'
    ),
  ];
  slides.forEach((slide, index) => {
    if (slide.dataset.pdpAnalyzed) return;
    const img = slide.querySelector('.product-media img');
    if (!img) return;
    slide.dataset.pdpAnalyzed = 'true';
    const set = async () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      slide.style.setProperty('--media-preview-ratio', (img.naturalWidth / img.naturalHeight).toFixed(4));
      if (await detectBleed(img.currentSrc || img.src)) {
        slide.setAttribute('data-pdp-bleed', '');
        document
          .querySelectorAll(
            `main[data-template^="product"] media-gallery.media-gallery--carousel slideshow-controls[thumbnails] .slideshow-controls__thumbnails > :nth-child(${index + 1})`
          )
          .forEach((thumb) => thumb.setAttribute('data-pdp-bleed', ''));
      }
    };
    if (img.complete) set();
    else img.addEventListener('load', set, { once: true });
  });
}

/* Full-bleed detection: canvas-style product renders have transparent or
   tint/near-white edges, so contain/capped-crop letterboxes invisibly.
   A full-bleed photo (texture swatch, lifestyle shot) has real content at
   its edges — letterboxing it shows hard seams against the tint, so those
   keep true cover. Samples the border of a tiny CORS copy of the image;
   any failure resolves false (capped — today's behavior). */
function detectBleed(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(false);
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = () => {
      try {
        const size = 32;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(probe, 0, 0, size, size);
        const data = ctx.getImageData(0, 0, size, size).data;
        let border = 0;
        let backgroundLike = 0;
        for (let y = 0; y < size; y++) {
          for (let x = 0; x < size; x++) {
            if (x !== 0 && x !== size - 1 && y !== 0 && y !== size - 1) continue;
            const i = (y * size + x) * 4;
            border++;
            const [r, g, b, a] = [data[i], data[i + 1], data[i + 2], data[i + 3]];
            const isTintish = Math.abs(r - 244) <= 40 && Math.abs(g - 243) <= 40 && Math.abs(b - 239) <= 40;
            const isWhitish = r >= 236 && g >= 236 && b >= 236;
            if (a < 16 || isTintish || isWhitish) backgroundLike++;
          }
        }
        resolve(backgroundLike / border < 0.7);
      } catch {
        resolve(false);
      }
    };
    probe.onerror = () => resolve(false);
    probe.src = /[?&]width=\d+/.test(src) ? src.replace(/([?&])width=\d+/, '$1width=64') : src;
  });
}

/* The "Gifting Options" app block caps itself at max-width: 640px with an
   !important rule inside its shadow root, which outside CSS cannot beat —
   so a small overriding style is appended INSIDE the (open) shadow root,
   where later source order wins. Stretches the card to the column width,
   matching the add-to-cart button. */
function fixGiftingWidth() {
  document
    .querySelectorAll('main[data-template^="product"] .product-details wrapped-extension')
    .forEach((el) => {
      const root = el.shadowRoot;
      if (!root || root.querySelector('.commune-gifting-width')) return;
      const style = document.createElement('style');
      style.className = 'commune-gifting-width';
      /* matches the app's own :host(wrapped-extension) specificity; wins on source order */
      style.textContent = ':host(wrapped-extension) { max-width: none !important; width: 100% !important; }';
      root.appendChild(style);
    });
}

let scheduled = false;
function init() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    document.querySelectorAll(RAIL_SELECTOR).forEach(setupRail);
    tagSlideRatios();
    fixGiftingWidth();
  });
}

init();
/* media-gallery replaces itself on variant change — re-scan for new rails */
new MutationObserver(init).observe(document.body, { childList: true, subtree: true });

/* --- 2. top-chrome tracking for the full-height media frame --- */

function setupFrameHeight() {
  const media = document.querySelector('main[data-template^="product"] .product-information__media');
  if (!media) return;

  let chromeHeight = 0;

  const apply = () => {
    /* The frame BOX breathes: on landing it runs from the navbar's bottom
       to the fold, and it grows to the full viewport as the chrome scrolls
       away (always max height until the sticky pin). The IMAGE inside does
       not rescale with it: CSS gives it a fixed inner box sized from
       --pdp-chrome-static (the constant at-rest chrome height), centred in
       the growing frame — the growth reveals matching tint, not a zoom.
       Under an always-sticky nav the chrome floor is the nav itself. */
    const stickyHeader = document.querySelector('#header-component[sticky="always"]');
    const visible = stickyHeader
      ? stickyHeader.offsetHeight
      : Math.max(0, chromeHeight - window.scrollY);
    media.style.setProperty('--pdp-chrome-visible', `${visible.toFixed(1)}px`);
    media.style.setProperty('--pdp-chrome-static', `${chromeHeight.toFixed(1)}px`);

    /* aspect ratio for the CSS crop cap — measured from the image's fixed
       inner box so it stays constant during the frame's growth (falls back
       to the live container before the image lays out) */
    const container = media.querySelector('media-gallery.media-gallery--carousel slideshow-container');
    if (container) {
      const img = container.querySelector('.product-media img');
      const referenceHeight = img?.clientHeight || container.clientHeight || Math.max(500, window.innerHeight - visible);
      media.style.setProperty('--pdp-frame-ar', (container.clientWidth / referenceHeight).toFixed(4));
    }

    /* Pin the slide strip's scroll position while the rail breathes. The
       rail width tracks frame height, so the strip resizes during the first
       pixels of page scroll and the current slide's snap position moves —
       the browser only corrects it when the scroll gesture ends, which
       reads as a sideways pop on every slide but the first. Correcting
       scrollLeft here, every frame, leaves nothing to snap. */
    const component = media.querySelector('media-gallery.media-gallery--carousel slideshow-component');
    const strip = component?.querySelector('slideshow-slides');
    /* desktop/tablet only — mobile keeps native swiping, which this would fight */
    if (strip && typeof component.current === 'number' && matchMedia('(min-width: 750px)').matches) {
      const slide = strip.querySelectorAll('slideshow-slide')[component.current];
      if (slide && Math.abs(strip.scrollLeft - slide.offsetLeft) > 0.5) {
        strip.scrollLeft = slide.offsetLeft;
      }
    }
  };

  /* the media column's document offset = everything in flow above it */
  const measure = () => {
    chromeHeight = media.getBoundingClientRect().top + window.scrollY;
    apply();
    measureDetailsShift();
  };

  /* Horizontal centering of the details column: the media frame bleeds to
     the viewport's left edge but the grid stops at the page margin on the
     right, so the info block hugs the frame. Expose half the surplus
     (page margin minus the column gutter) so the CSS can shift the column
     into the visual centre of frame-edge-to-viewport-edge. */
  const measureDetailsShift = () => {
    const grid = document.querySelector('main[data-template^="product"] .product-information__grid');
    const details = grid?.querySelector('.product-details');
    if (!grid || !details) return;
    const pageMargin = Math.max(0, document.documentElement.clientWidth - grid.getBoundingClientRect().right);
    const gap = parseFloat(getComputedStyle(details).getPropertyValue('--gap')) || 0;
    const shift = Math.max(0, (pageMargin - gap) / 2);
    grid.style.setProperty('--pdp-details-shift', `${shift.toFixed(1)}px`);

    /* Media column width: the frame's right edge must land exactly on the
       layout centreline (where the header monogram sits) — a plain 50vw
       misses it by half the scrollbar plus the column gutter. */
    const mediaColumn = document.documentElement.clientWidth / 2 + gap / 2;
    grid.style.setProperty('--pdp-media-col', `${mediaColumn.toFixed(1)}px`);
  };

  let ticking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        apply();
      });
    },
    { passive: true }
  );
  window.addEventListener('resize', measure);
  window.addEventListener('load', measure);
  measure();
}

setupFrameHeight();

/* --- 3. slide changes: instant jump + quick fade (replaces animated scroll) --- */

customElements.whenDefined('slideshow-component').then(() => {
  const proto = customElements.get('slideshow-component').prototype;
  const originalSelect = proto.select;

  proto.select = function (input, event, options = {}) {
    const isMainPdpGallery =
      matchMedia('(min-width: 750px)').matches &&
      this.closest('main[data-template^="product"]') &&
      this.closest('media-gallery.media-gallery--carousel') &&
      !this.closest('.dialog-zoomed-gallery');

    const requested =
      typeof input === 'number' ? input : typeof input === 'string' ? parseInt(input, 10) : NaN;
    const isNoop = !Number.isNaN(requested) && requested === this.current;

    if (!isMainPdpGallery || options.animate === false || isNoop) {
      return originalSelect.call(this, input, event, options);
    }

    const result = originalSelect.call(this, input, event, { ...options, animate: false });
    this.querySelector('slideshow-slides')?.animate(
      { opacity: [0, 1] },
      { duration: 200, easing: 'ease-out' }
    );
    return result;
  };
});

/* --- no mouse-drag side-swipe on the main gallery (desktop) ---
   overflow-x: hidden (commune-pdp-media-bg.css) stops trackpad scrolling,
   but the theme's slideshow also drags slides via a mousedown handler that
   scrolls programmatically. Swallow mousedown before it reaches that
   handler; click-to-zoom still works (click events are unaffected). */
document.addEventListener(
  'mousedown',
  (event) => {
    if (!matchMedia('(min-width: 750px)').matches) return;
    if (!(event.target instanceof Element)) return;
    if (
      event.target.closest(
        'main[data-template^="product"] media-gallery.media-gallery--carousel slideshow-slides'
      )
    ) {
      event.stopPropagation();
    }
  },
  true
);

/* --- 4. lightbox: fades instead of the view-transition morph (desktop) --- */

customElements.whenDefined('zoom-dialog').then(() => {
  const proto = customElements.get('zoom-dialog').prototype;
  const isDesktop = () => matchMedia('(min-width: 750px)').matches;

  const originalOpen = proto.open;
  proto.open = function (index, event) {
    if (!isDesktop()) return originalOpen.call(this, index, event);

    event.preventDefault();
    const { dialog } = this.refs;
    dialog.showModal();
    this.selectThumbnail(index, { behavior: 'instant' });
    dialog.animate({ opacity: [0, 1] }, { duration: 250, easing: 'ease-out' });
  };

  const originalClose = proto.close;
  proto.close = async function () {
    if (!isDesktop()) return originalClose.call(this);

    const { dialog } = this.refs;
    try {
      dialog.getAnimations().forEach((animation) => animation.cancel());
      await dialog.animate({ opacity: [1, 0] }, { duration: 200, easing: 'ease-in' }).finished;
    } catch {
      /* fade interrupted — fall through and close anyway */
    } finally {
      if (dialog.open) this.closeDialog();
    }
  };

  const originalThumbnailClick = proto.handleThumbnailClick;
  proto.handleThumbnailClick = function (index) {
    if (!isDesktop()) return originalThumbnailClick.call(this, index);

    this.selectThumbnail(index, { behavior: 'instant' });
    this.refs.dialog
      .querySelector('.dialog-zoomed-gallery')
      ?.animate({ opacity: [0, 1] }, { duration: 200, easing: 'ease-out' });
  };
});
